import { useMemo } from 'react';

import {
  SUGGESTED_ANSWER_EXAMPLE,
  SUGGESTED_ANSWER_FIXED,
  SUGGESTED_ANSWER_VARY,
} from 'global/constants';
import type { RichTextAnswerFragment } from '../../../present/activity-answers.graphql.generated';
import { RichtextDisplay } from '../../teaching-notes/teaching-notes-drawer.style';
import { PStyled } from 'approot/shared/styles/typography.style';
import SuggestedAnswerTitle from '../suggested-answer-title';
import { Box } from 'approot/shared/design-system/box/box.style';
import { useFindComponentDimensions } from '../../component.hooks';
import { SettingsGroup } from 'approot/activity-builder/settings/settings.style';
import ActivityBuilderComponentPreview from 'approot/activity-builder/page/activity-builder-component-preview';
import TablePrint from '../../table/table-print';
import { ComponentTable } from '../../component.constants';
import { COMPONENT_SELECTOR } from '../../shared/logic/get-component-selector-id';
import {
  getRichtextValue,
  isRichtextValueEmpty,
} from '../../shared/logic/get-richtext-value';
import {
  CONTAINER_WIDTH,
  SUGGESTED_ANSWER_BASE_RICHTEXT,
} from '../suggested-answer.constants';
import { getRichtextAnswer } from '../../shared/logic/get-richtext-answer';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import ActivityPresentModal from 'approot/activity-builder/present/activity-present-modal';
import { getSubActivityLabel } from '../suggested-answer.utils';
import SuggestedAnswerExampleRichtext from './suggested-answer-example-richtext';
import SuggestedAnswerPreview from './suggested-answer-preview';
import { SuggestedAnswerPreviewContainer } from '../suggested-answer.style';
import SuggestedAnswerViewBadges from './suggested-answer-view-badges';
import { useGetRichtextAnswer } from '../suggested-answer.hooks';

type Props = {
  component: ComponentTable;
  isUnitQuiz: boolean;
};

const SuggestedAnswerTableView = ({ component, isUnitQuiz }: Props) => {
  const { id, subActivityProperties, suggestedAnswerProperties } = component;
  const answerType = suggestedAnswerProperties?.suggestedAnswerType;

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const { width, height } = useFindComponentDimensions(id, COMPONENT_SELECTOR);

  const hasNoRichTextData = useMemo(
    () =>
      answerType !== SUGGESTED_ANSWER_VARY &&
      suggestedAnswerProperties?.suggestedAnswers?.every(answer =>
        isRichtextValueEmpty(getRichtextAnswer(answer))
      ),
    [answerType, suggestedAnswerProperties]
  );

  const isAnswersTableEmpty = useMemo(
    () =>
      answerType !== SUGGESTED_ANSWER_VARY &&
      suggestedAnswerProperties?.suggestedAnswers
        ?.filter(answer => answer.answerReferences !== null)
        .every(answer => isRichtextValueEmpty(getRichtextAnswer(answer))),
    [answerType, suggestedAnswerProperties]
  );

  const answer = useGetRichtextAnswer({
    componentId: id,
    suggestedAnswerProperties,
  });

  return hasNoRichTextData && !isUnitQuiz ? null : (
    <Box>
      {isVisible && (
        <ActivityPresentModal
          isVisible
          title="Viewing Answers"
          onClose={closeModal}
          size={width}
        >
          <SuggestedAnswerTitle
            isLarge
            label={subActivityProperties?.subActivityLabel}
            instructions={subActivityProperties?.instructions}
          />
          <TablePrint
            showAnswers
            {...component}
            subActivityProperties={getSubActivityLabel(subActivityProperties)}
          />
        </ActivityPresentModal>
      )}

      <SuggestedAnswerTitle
        isSmall
        label={subActivityProperties?.subActivityLabel}
        instructions={subActivityProperties?.instructions}
      />

      <SuggestedAnswerViewBadges
        isUnitQuiz={isUnitQuiz}
        suggestedAnswerScore={suggestedAnswerProperties?.suggestedAnswersScore}
        isExampleAnswer={
          answerType === SUGGESTED_ANSWER_EXAMPLE &&
          !isRichtextValueEmpty(answer)
        }
        isFixedAnswer={answerType === SUGGESTED_ANSWER_FIXED}
      />

      {answerType === SUGGESTED_ANSWER_VARY ? (
        <PStyled>Answers may vary</PStyled>
      ) : (
        <>
          {!isAnswersTableEmpty && (
            <SettingsGroup marginBottom={8}>
              <SuggestedAnswerPreviewContainer>
                <ActivityBuilderComponentPreview
                  container={{
                    width: CONTAINER_WIDTH,
                  }}
                  content={{
                    width,
                    height,
                  }}
                >
                  <TablePrint
                    {...component}
                    subActivityProperties={getSubActivityLabel(
                      subActivityProperties
                    )}
                  />
                </ActivityBuilderComponentPreview>
              </SuggestedAnswerPreviewContainer>
              <SuggestedAnswerPreview
                ariaLabel="View table response answers"
                onClick={openModal}
              />
            </SettingsGroup>
          )}

          {answerType === SUGGESTED_ANSWER_EXAMPLE ? (
            <SuggestedAnswerExampleRichtext
              id={id}
              suggestedAnswerProperties={suggestedAnswerProperties}
            />
          ) : (
            <>
              {suggestedAnswerProperties?.suggestedAnswers?.map(
                suggestedData => {
                  const isEmpty = isRichtextValueEmpty(
                    (suggestedData?.answer as RichTextAnswerFragment)?.richtext
                  );
                  return !isEmpty && suggestedData.answerReferences === null ? (
                    <RichtextDisplay
                      key={suggestedData.uuid}
                      dangerouslySetInnerHTML={{
                        __html: getRichtextValue(
                          getRichtextAnswer(suggestedData),
                          SUGGESTED_ANSWER_BASE_RICHTEXT
                        ),
                      }}
                    />
                  ) : null;
                }
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default SuggestedAnswerTableView;
